import { useEffect, useRef } from 'react';
import { pushEvent } from '../utils/gtm';
import { isGtmEnabled } from '../utils/getGtmDomain';
import { getAttribution } from '../utils/attribution';
import { SS_ENGAGEMENT_TIME_SENT } from '../configs/storageKeys';
import { GA4_EVENTS, ENGAGEMENT_TIME_THRESHOLDS } from '../constants/ga4Events';

/**
 * Fires engagement_time_threshold at 10/30/60s of *active* time — each once per session.
 * Active = tab visible; time pauses on visibilitychange (GA4 engagement_time semantics).
 * Timer resets each page load; sessionStorage prevents re-firing an already-sent threshold.
 * Mount once at app root (not inside <Routes>); no router dependency.
 */
export function EngagementTimeTracker() {
  // In-memory guard so we don't touch sessionStorage every tick.
  const firedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    // GTM off → no events would ever send; skip timers/listeners entirely.
    if (!isGtmEnabled()) return;

    firedRef.current = new Set();
    let accumulatedMs = 0;
    // performance.now() is monotonic — immune to system clock jumps (NTP / manual change).
    let lastResume: number | null =
      document.visibilityState === 'visible' ? performance.now() : null;
    let timerId: ReturnType<typeof setTimeout> | undefined;

    const activeMs = () =>
      accumulatedMs + (lastResume != null ? performance.now() - lastResume : 0);

    const isSent = (sec: number) => {
      if (firedRef.current.has(sec)) return true;
      try {
        if (sessionStorage.getItem(`${SS_ENGAGEMENT_TIME_SENT}:${sec}`)) {
          firedRef.current.add(sec);
          return true;
        }
      } catch {}
      return false;
    };

    const send = (sec: number) => {
      if (isSent(sec)) return;
      // Wrapped so an unexpected throw (e.g. getAttribution) can't abort tick() before it
      // reschedules the next threshold — one bad send must not kill the whole session's chain.
      try {
        // current_path / attribution come from the shared context (read live at fire time).
        const sent = pushEvent(GA4_EVENTS.ENGAGEMENT_TIME_THRESHOLD, {
          threshold_sec: sec,
          ...getAttribution(),
        });
        if (sent) {
          firedRef.current.add(sec);
          try { sessionStorage.setItem(`${SS_ENGAGEMENT_TIME_SENT}:${sec}`, '1'); } catch {}
        }
      } catch {}
    };

    // Single self-rescheduling timer. Fires every threshold already reached (catch-up after a
    // long hidden/sleep gap) and arms the next *future* unsent one. A past-but-unsent threshold
    // (e.g. pushEvent failed) is NOT rescheduled here → no busy loop; it gets a natural retry on
    // the next tick. Relies on ENGAGEMENT_TIME_THRESHOLDS being ascending.
    const tick = () => {
      if (timerId) { clearTimeout(timerId); timerId = undefined; }
      if (lastResume == null) return;             // paused (hidden) → no timer
      const ms = activeMs();
      for (const sec of ENGAGEMENT_TIME_THRESHOLDS) {
        if (ms >= sec * 1000) send(sec);          // send() dedups internally
      }
      const next = ENGAGEMENT_TIME_THRESHOLDS.find((s) => !isSent(s) && s * 1000 > ms);
      if (next == null) return;                   // no future threshold left → done
      timerId = setTimeout(tick, next * 1000 - ms);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (lastResume != null) { accumulatedMs += performance.now() - lastResume; lastResume = null; }
        if (timerId) { clearTimeout(timerId); timerId = undefined; }
      } else if (lastResume == null) {
        lastResume = performance.now();
        tick();
      }
    };

    tick();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timerId) clearTimeout(timerId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return null;
}
